import { format, subMonths, subYears } from "date-fns";

/**
 * Result date-range presets for step 1 of the "Sync EHR data" modal, resolved
 * to `from`/`to` bounds (`yyyy-MM-dd`, null = unbounded). The Keragon trigger
 * only takes a lower bound (`since`), so `to` is applied to the returned
 * readings client-side (isWithinRange).
 */
export type DateRangePreset = "6m" | "1y" | "3y" | "all" | "custom";

export const DATE_RANGE_PRESETS: { key: DateRangePreset; label: string }[] = [
  { key: "6m", label: "Last 6 months" },
  { key: "1y", label: "Last year" },
  { key: "3y", label: "Last 3 years" },
  { key: "all", label: "All available" },
  { key: "custom", label: "Custom" },
];

export type CustomRange = { startDate: Date | null; endDate: Date | null };

export type ResolvedRange = { from: string | null; to: string | null };

const day = (d: Date) => format(d, "yyyy-MM-dd");

export const resolveRange = (
  preset: DateRangePreset,
  custom: CustomRange,
  now: Date = new Date()
): ResolvedRange => {
  switch (preset) {
    case "6m":
      return { from: day(subMonths(now, 6)), to: day(now) };
    case "1y":
      return { from: day(subYears(now, 1)), to: day(now) };
    case "3y":
      return { from: day(subYears(now, 3)), to: day(now) };
    case "all":
      return { from: null, to: null };
    case "custom":
    default:
      return {
        from: custom.startDate ? day(custom.startDate) : null,
        to: custom.endDate ? day(custom.endDate) : null,
      };
  }
};

/** A custom range is invalid only when both ends are set the wrong way round. */
export const isCustomRangeInverted = (custom: CustomRange) =>
  Boolean(custom.startDate && custom.endDate && custom.startDate > custom.endDate);

/** Floor sent as `since` when the range has no start ("All available"). */
export const KERAGON_SINCE_FLOOR = "2020-01-01";

export const toSince = (range: ResolvedRange) => range.from ?? KERAGON_SINCE_FLOOR;

/** Inclusive on both ends; a null bound is open. `date` may carry a time. */
export const isWithinRange = (date: string, range: ResolvedRange) => {
  const d = date.slice(0, 10);
  return (!range.from || d >= range.from) && (!range.to || d <= range.to);
};
